import usePageTitle from "../../utils/hooks/usePageTitle";
import StatsCard from "../../components/dashboard/common/StatsCard";
import DashboardItem from "../../components/dashboard/common/DashboardItem";
import DashboardGrid from "../../components/dashboard/common/DashboardGrid";
import PieChartIcon from "../../assets/icons/PieChartIcon";
import ReportsCounterIcon from "../../assets/icons/ReportsCounterIcon";
import ReportsApprovedIcon from "../../assets/icons/ReportsApprovedIcon";
import ReportsReviewIcon from "../../assets/icons/ReportsReviewIcon";
import MoneyPendingIcon from "../../assets/icons/MoneyPendingIcon";
import { Bar } from "react-chartjs-2";
import { Pie } from "react-chartjs-2";
import PretestingProgramsList from "../../components/programs/PretestingProgramsList";
import {
		  Chart as ChartJS,
		  CategoryScale,
		  LinearScale,
		  BarElement,
		  ArcElement,
		  Title,
		  Tooltip,
		  Legend
		} from "chart.js";
		import { useEffect, useMemo, useState } from "react";
import {
			  getResearchersMonthlyActivity,
			  getResearchersOverview,
			  getOpenSlaBreaches,
			  getSlaBreaches
			} from "../../utils/api/endpoints/researcher/profile";
		import useUserCredentials from "../../utils/hooks/user/useUserCredentials";

	ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  ArcElement,
  Title,
  Tooltip,
  Legend
);

const ResearcherDashboard = () => {
	  usePageTitle("Dashboard");

	  const { id: currentUserId } = useUserCredentials();

		const [overview, setOverview] = useState<{
    totals: {
      totalResearchers: number;
      totalVulnerabilities: number;
      criticalFindings: number;
    };
    avgResponseTimeHours: number;
    severityDistribution: { critical: number; high: number; medium: number; low: number };
    vulnerabilityTypes?: { items: Array<{ label: string; count: number; percent: number }> };
  } | null>(null);
			  const [activity, setActivity] = useState<Array<{ month: string; count: number }>>([]);
		  const [slaAlerts, setSlaAlerts] = useState<
		    Array<{
		      retest_id: number;
		      report_id: number;
		      report_title?: string | null;
		      program_name?: string | null;
		      sla_deadline_at?: string | null;
		      sla_breached_at?: string | null;
		      alertType?: "warning" | "breached";
		      hoursLeft?: number;
		    }>
		  >([]);
			  const [slaBreaches, setSlaBreaches] = useState<
			    Array<{
			      retest_id: number;
			      report_id: number;
			      report_title?: string | null;
			      program_name?: string | null;
			      severity?: number | null;
			      severity_category?: string | null;
			      sla_deadline_at?: string | null;
			      sla_breached_at?: string | null;
			      status?: string | null;
			      penaltyPoints: number;
			    }>
			  >([]);
			  const [totalSlaPenaltyPoints, setTotalSlaPenaltyPoints] = useState<number>(0);
			  const [loading, setLoading] = useState<boolean>(true);

		  useEffect(() => {
		    let mounted = true;
		    (async () => {
			      try {
			        setLoading(true);
			        const [ov, ma, slaAlertRes, slaHistoryRes] = await Promise.all([
			          getResearchersOverview(),
			          getResearchersMonthlyActivity(12),
			          currentUserId ? getOpenSlaBreaches(currentUserId) : Promise.resolve({ items: [] }),
			          currentUserId ? getSlaBreaches(currentUserId) : Promise.resolve({ items: [], totalPenaltyPoints: 0 })
			        ]);
		        if (!mounted) return;
		        setOverview(ov);
		        setActivity(ma.series || []);
			        setSlaAlerts((slaAlertRes as any)?.items || []);
			        setSlaBreaches((slaHistoryRes as any)?.items || []);
			        setTotalSlaPenaltyPoints((slaHistoryRes as any)?.totalPenaltyPoints || 0);
		      } catch (e) {
		        // ignore
		      } finally {
		        if (mounted) setLoading(false);
		      }
		    })();
		    return () => {
		      mounted = false;
		    };
		  }, [currentUserId]);

  const monthlyChart = useMemo(() => {
    const formatMonthYear = (ym: string) => {
      const [y, m] = ym.split("-").map(Number);
      const d = new Date(Date.UTC(y || 1970, (m || 1) - 1, 1));
      const month = d.toLocaleString("en-US", { month: "short" });
      const year = d.getUTCFullYear();
      return `${month} ${year}`;
    };

    const labels = activity.map(a => formatMonthYear(a.month));
    const data = activity.map(a => a.count);
    return {
      data: {
        labels,
        datasets: [
          {
            label: "Vulnerabilities",
            data,
            backgroundColor: "#0F2771",
            borderRadius: 0,
            barThickness: 36,
            maxBarThickness: 40
          }
        ]
      },
      options: {
        responsive: true,
        maintainAspectRatio: false,
        plugins: {
          legend: {
            display: true,
            position: "bottom" as const,
            labels: {
              usePointStyle: true,
              pointStyle: "rect",
              padding: 20,
              boxWidth: 16,
              boxHeight: 16,
              color: "#0B1739",
              font: { size: 14, weight: "600" }
            }
          },
          tooltip: { enabled: true }
        },
        layout: { padding: { top: 8, right: 8, left: 8, bottom: 0 } },
        scales: {
          x: {
            grid: { display: true, color: "rgba(0,0,0,0.08)", borderDash: [4,4] },
            ticks: { color: "#6B7280", font: { size: 14 } },
            border: { display: false }
          },
          y: {
            beginAtZero: true,
            grid: { color: "rgba(0,0,0,0.08)", borderDash: [4,4] },
            ticks: { color: "#6B7280", font: { size: 14 } },
            border: { display: false }
          }
        },
        elements: { bar: { borderRadius: 0 } }
      }
    } as any;
  }, [activity]);

  return (
    <div className="bg-[#F9FAFB] p-6">
      {/* Section header */}
      <div className="mb-6">
        <h1 className="text-3xl font-extrabold tracking-tight text-[#0B1739]">
          Security Researchers
        </h1>
        <p className="mt-2 text-base text-gray-500">
          Comprehensive overview of all security researchers and their performance
        </p>
      </div>
		      {/* SLA approaching-breach alert banner (within next 4 hours) */}
		      {slaAlerts.filter(a => a.alertType === "warning").length > 0 && (
		        <div className="mb-4 rounded-2xl border border-amber-100 bg-gradient-to-r from-amber-50 via-yellow-50 to-white p-4 shadow-sm">
		          <div className="flex items-start gap-3">
		            <div className="mt-1 flex h-8 w-8 items-center justify-center rounded-full bg-amber-100 text-amber-700">
		              <svg
		                viewBox="0 0 24 24"
		                className="h-4 w-4"
		                fill="none"
		                stroke="currentColor"
		                strokeWidth="2"
		              >
		                <path d="M12 8v4" strokeLinecap="round" strokeLinejoin="round" />
		                <path d="M12 16h.01" strokeLinecap="round" strokeLinejoin="round" />
		                <path
		                  d="M10.29 3.86 2.82 17.02A1.6 1.6 0 0 0 4.21 19.4h15.58a1.6 1.6 0 0 0 1.39-2.38L13.71 3.86a1.6 1.6 0 0 0-2.82 0Z"
		                  strokeLinecap="round"
		                  strokeLinejoin="round"
		                />
		              </svg>
		            </div>
		            <div className="flex-1">
		              <div className="flex flex-col gap-1 sm:flex-row sm:items-center sm:justify-between">
		                <div>
		                  <h2 className="text-sm font-semibold text-[#92400E]">
		                    Retest SLA approaching — less than 4 hours left
		                  </h2>
		                  <p className="mt-1 text-xs text-gray-600">
		                    You have {slaAlerts.filter(a => a.alertType === "warning").length} retest
		                    {slaAlerts.filter(a => a.alertType === "warning").length > 1 ? "s" : ""} where
		                    the 24h SLA will breach in under 4 hours.
		                  </p>
		                </div>
		              </div>
		              <div className="mt-3 space-y-2">
		                {slaAlerts
		                  .filter(a => a.alertType === "warning")
		                  .slice(0, 3)
		                  .map(alert => (
		                    <div
		                      key={alert.retest_id}
		                      className="flex items-center justify-between rounded-lg bg-white/80 px-3 py-2 text-xs shadow-sm"
		                    >
		                      <div className="min-w-0">
		                        <p className="truncate font-medium text-gray-900">
		                          #{alert.report_id} · {alert.report_title || "Retest"}
		                        </p>
		                        <p className="mt-0.5 text-[11px] text-gray-500">
		                          {alert.program_name || "Program"}
		                          {typeof alert.hoursLeft === "number" && (
		                            <span className="ml-2 font-medium text-amber-700">
		                              (~{alert.hoursLeft.toFixed(1)}h left)
		                            </span>
		                          )}
		                        </p>
		                      </div>
		                      <a
		                        href={`/dashboard/retests/${alert.retest_id}`}
		                        className="ml-3 inline-flex items-center rounded-full border border-amber-200 px-3 py-1 text-[11px] font-medium text-amber-800 hover:bg-amber-50"
		                      >
		                        View retest
		                      </a>
		                    </div>
		                  ))}
		              </div>
		            </div>
		          </div>
		        </div>
		      )}

		      {/* SLA breach alert banner (open breached retests without action) */}
		      {slaAlerts.filter(a => a.alertType === "breached" || !a.alertType).length > 0 && (
		        <div className="mb-6 rounded-2xl border border-red-100 bg-gradient-to-r from-red-50 via-amber-50 to-white p-4 shadow-sm">
		          <div className="flex items-start gap-3">
		            <div className="mt-1 flex h-8 w-8 items-center justify-center rounded-full bg-red-100 text-red-600">
		              <svg
		                viewBox="0 0 24 24"
		                className="h-4 w-4"
		                fill="none"
		                stroke="currentColor"
		                strokeWidth="2"
		              >
		                <path d="M12 9v4" strokeLinecap="round" strokeLinejoin="round" />
		                <path d="M12 17h.01" strokeLinecap="round" strokeLinejoin="round" />
		                <path
		                  d="M10.29 3.86 2.82 17.02A1.6 1.6 0 0 0 4.21 19.4h15.58a1.6 1.6 0 0 0 1.39-2.38L13.71 3.86a1.6 1.6 0 0 0-2.82 0Z"
		                  strokeLinecap="round"
		                  strokeLinejoin="round"
		                />
		              </svg>
		            </div>
		            <div className="flex-1">
		              <div className="flex flex-col gap-1 sm:flex-row sm:items-center sm:justify-between">
		                <div>
		                  <h2 className="text-sm font-semibold text-[#7F1D1D]">
		                    SLA breached on retest
		                    {slaAlerts.filter(a => a.alertType === "breached" || !a.alertType).length > 1
		                      ? "s"
		                      : ""} — action required
		                  </h2>
		                  <p className="mt-1 text-xs text-gray-600">
		                    You have {" "}
		                    {slaAlerts.filter(a => a.alertType === "breached" || !a.alertType).length} retest
		                    {slaAlerts.filter(a => a.alertType === "breached" || !a.alertType).length > 1
		                      ? "s"
		                      : ""} where the 24h SLA was breached and no retest action has been
		                    taken yet.
		                  </p>
		                </div>
		              </div>
		              <div className="mt-3 space-y-2">
		                {slaAlerts
		                  .filter(a => a.alertType === "breached" || !a.alertType)
		                  .slice(0, 3)
		                  .map(alert => (
		                    <div
		                      key={alert.retest_id}
		                      className="flex items-center justify-between rounded-lg bg-white/70 px-3 py-2 text-xs shadow-sm"
		                    >
		                      <div className="min-w-0">
		                        <p className="truncate font-medium text-gray-900">
		                          #{alert.report_id} · {alert.report_title || "Retest"}
		                        </p>
		                        <p className="mt-0.5 text-[11px] text-gray-500">
		                          {alert.program_name || "Program"}
		                        </p>
		                      </div>
		                      <a
		                        href={`/dashboard/retests/${alert.retest_id}`}
		                        className="ml-3 inline-flex items-center rounded-full border border-red-200 px-3 py-1 text-[11px] font-medium text-red-700 hover:bg-red-50"
		                      >
		                        View retest
		                      </a>
		                    </div>
		                  ))}
		              </div>
		            </div>
		          </div>
		        </div>
		      )}
		      <div className="grid grid-cols-1 gap-6 md:grid-cols-2 xl:grid-cols-4">
          <StatsCard
            title="Total Researchers"
            value={overview?.totals.totalResearchers ?? 0}
            loading={loading}
            rightIconSrc="/icons/box1.svg"
            rightIconBgClassName="bg-[#EEF4FF]"
          />
          <StatsCard
            title="Total Vulnerabilities"
            value={overview?.totals.totalVulnerabilities ?? 0}
            loading={loading}
            rightIconSrc="/icons/box2.svg"
            rightIconBgClassName="bg-[#FFF7E6]"
          />
          <StatsCard
            title="Critical Findings"
            value={overview?.totals.criticalFindings ?? 0}
            loading={loading}
            rightIconSrc="/icons/box3.svg"
            rightIconBgClassName="bg-[#FDEDED]"
          />
          <StatsCard
            title="Avg. Response Time"
            value={(overview?.avgResponseTimeHours ?? 0).toFixed(1)}
            suffix="hours"
            loading={loading}
            rightIconSrc="/icons/box4.svg"
            rightIconBgClassName="bg-[#E6FBF6]"
          />
      </div>
			      {/* SLA breach history section - premium compact card */}
			      {slaBreaches.length > 0 && (
			        <div className="mt-6 rounded-2xl border border-red-100 bg-gradient-to-r from-red-50 via-rose-50 to-white p-6 shadow-sm">
			          <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
			            <div className="flex items-start gap-3">
			              <div className="mt-1 flex h-9 w-9 items-center justify-center rounded-full bg-red-100 text-red-600">
			                <svg
			                  viewBox="0 0 24 24"
			                  className="h-4 w-4"
			                  fill="none"
			                  stroke="currentColor"
			                  strokeWidth="2"
			                >
			                  <path d="M12 9v4" strokeLinecap="round" strokeLinejoin="round" />
			                  <path d="M12 17h.01" strokeLinecap="round" strokeLinejoin="round" />
			                  <path
			                    d="M10.29 3.86 2.82 17.02A1.6 1.6 0 0 0 4.21 19.4h15.58a1.6 1.6 0 0 0 1.39-2.38L13.71 3.86a1.6 1.6 0 0 0-2.82 0Z"
			                    strokeLinecap="round"
			                    strokeLinejoin="round"
			                  />
			                </svg>
			              </div>
			              <div>
			                <h3 className="text-sm font-semibold text-[#7F1D1D]">
			                  SLA Breach History
			                </h3>
			                <p className="mt-1 text-xs text-red-900/80">
			                  Retests that breached the 24h SLA and reduced your reputation
			                  points.
			                </p>
			              </div>
			            </div>
			            <div className="text-right">
			              <p className="text-[10px] font-semibold uppercase tracking-wide text-red-900/70">
			                Total SLA penalty
			              </p>
			              <p className="mt-1 text-base font-semibold text-red-700">
			                -{totalSlaPenaltyPoints} pts
			              </p>
			            </div>
			          </div>
			          <div className="mt-4 divide-y divide-red-100/70 rounded-xl bg-white/80">
			            {slaBreaches.slice(0, 5).map(item => {
			              const breachedAt = item.sla_breached_at
			                ? new Date(item.sla_breached_at)
			                : null;
			              const breachedLabel = breachedAt
			                ? breachedAt.toLocaleString("en-US", {
			                    month: "short",
			                    day: "numeric",
			                    year: "numeric",
			                    hour: "numeric",
			                    minute: "2-digit",
			                    hour12: true
			                  })
			                : "-";
			              const severityLabel = item.severity_category || "";
			              const isInfo = ["info", "informational", "none"].includes(severityLabel.toLowerCase());
			              const severityColor =
			                severityLabel.toLowerCase() === "critical"
			                  ? "bg-red-100 text-red-700"
			                  : severityLabel.toLowerCase() === "high"
			                  ? "bg-orange-100 text-orange-700"
			                  : severityLabel.toLowerCase() === "medium"
			                  ? "bg-amber-100 text-amber-700"
			                  : severityLabel.toLowerCase() === "low"
			                  ? "bg-emerald-100 text-emerald-700"
			                  : isInfo
			                  ? "bg-blue-100 text-blue-700"
			                  : "bg-gray-100 text-gray-700";
			              return (
			                <div
			                  key={item.retest_id}
			                  className="flex items-center justify-between px-3 py-2.5 text-xs"
			                >
			                  <div className="min-w-0">
			                    <p className="truncate font-medium text-gray-900">
			                      #{item.report_id} · {item.report_title || "Retest"}
			                    </p>
			                    <div className="mt-0.5 flex flex-wrap items-center gap-1.5 text-[11px] text-gray-500">
			                      <span className="truncate max-w-[160px]">
			                        {item.program_name || "Program"}
			                      </span>
			                      {severityLabel && (
			                        <span
			                          className={`inline-flex items-center rounded-full px-2 py-0.5 text-[10px] font-medium ${severityColor}`}
			                        >
			                          {severityLabel.toLowerCase() === "none" ? "Info" : severityLabel}
			                        </span>
			                      )}
			                      <span className="text-[10px] text-gray-400">
			                        Breached on {breachedLabel}
			                      </span>
			                    </div>
			                  </div>
			                  <div className="ml-3 text-right">
			                    <p className="text-[11px] font-semibold text-red-600">
			                      -{item.penaltyPoints} pts
			                    </p>
			                    <a
			                      href={`/dashboard/retests/${item.retest_id}`}
			                      className="mt-1 inline-flex items-center rounded-full border border-gray-200 px-3 py-0.5 text-[10px] font-medium text-gray-700 hover:border-red-200 hover:bg-red-50/60 hover:text-red-700"
			                    >
			                      View retest
			                    </a>
			                  </div>
			                </div>
			              );
			            })}
			          </div>
			          {slaBreaches.length > 5 && (
			            <p className="mt-3 text-[11px] text-gray-500">
			              Showing latest 5 of {slaBreaches.length} SLA breaches.
			            </p>
			          )}
			        </div>
			      )}
      <div className="mt-6 grid grid-cols-1 gap-6 xl:grid-cols-12">
        <div className="xl:col-span-7 rounded-2xl bg-white p-6 shadow-lg ring-1 ring-gray-100">
          <div className="mb-4 flex items-center justify-between">
            <h3 className="text-lg font-semibold text-[#0B1739]">Monthly Activity</h3>
            <span className="inline-flex h-8 w-8 items-center justify-center rounded-full ">
              <svg viewBox="0 0 24 24" className="h-4 w-4 text-[#0B1739]" fill="currentColor" aria-hidden>
                <rect x="4" y="12" width="3" height="8" rx="1" />
                <rect x="10.5" y="9" width="3" height="11" rx="1" />
                <rect x="17" y="6" width="3" height="14" rx="1" />
              </svg>
            </span>
          </div>
          <div className="h-[340px]">
            {loading ? (
              <div className="flex h-full flex-col items-center justify-center gap-2">
                <div className="h-8 w-8 animate-spin rounded-full border-2 border-gray-200 border-t-[#0F2771]" />
                <p className="text-xs text-gray-400">Loading monthly activity...</p>
              </div>
            ) : (
              <Bar data={monthlyChart.data} options={monthlyChart.options} />
            )}
          </div>
        </div>
        <div className="xl:col-span-5 rounded-2xl bg-white p-6 shadow-lg ring-1 ring-gray-100">
          <div className="mb-4 flex items-center justify-between">
            <h3 className="text-lg font-semibold text-[#0B1739]">Vulnerability Domain</h3>
            <span className="inline-flex h-8 w-8 items-center justify-center rounded-full">
              <img src="/icons/box5.svg" alt="types" className="h-4 w-4" />
            </span>
          </div>
          {loading ? (
            <div className="flex h-[320px] flex-col items-center justify-center gap-2">
              <div className="h-8 w-8 animate-spin rounded-full border-2 border-gray-200 border-t-[#0F2771]" />
              <p className="text-xs text-gray-400">Loading vulnerability domains...</p>
            </div>
          ) : (() => {
            const items = (overview as any)?.vulnerabilityTypes?.items || [] as Array<{ label: string; count: number; percent: number }>;
            const formatLabel = (raw: string) => {
              if (!raw) return raw;
              const r = String(raw).trim();
              const lower = r.toLowerCase();
              if (lower === "api" || r === "API" || r === "API Security") return "API";
              if (lower === "web security" || lower === "web") return "Web";
              if (lower === "ios") return r.includes("Security") ? "iOS Security" : "iOS";
              // capitalize each word
              return r
                .toLowerCase()
                .split(/\s+/)
                .map(w => w.charAt(0).toUpperCase() + w.slice(1))
                .join(" ");
            };
            const labels = items.map((i: { label: string; percent: number }) => formatLabel(i.label));
            const data = items.map((i: { count: number }) => i.count);
            if (!data.length) return <p className="text-sm text-gray-500">No data</p>;
            const colors = ["#0F2771", "#2563EB", "#34D399", "#F59E0B", "#EF4444", "#475569"];
            
            const totalsum = data.reduce((a:number,b:number)=>a+b,0) || 1;
            return (
              <div className="mt-8 h-[320px] flex items-center justify-between">
                <div className="w-[280px] h-[280px]">
                  <Pie
                    data={{
                      labels,
                      datasets: [
                        { 
                          data, 
                          backgroundColor: colors.slice(0, data.length), 
                          borderWidth: 0
                        }
                      ]
                    }}
                    options={{
                      maintainAspectRatio: false,
                      plugins: { 
                        legend: { 
                          display: false 
                        },
                        tooltip: {
                          callbacks: {
                            label: function(context) {
                              return context.label + ': ' + context.parsed + ' vulnerabilities';
                            }
                          }
                        }
                      },
                      layout: {
                        padding: 20
                      }
                    }}
                  />
                </div>
                {/* Right-side legend with labels and percentages */}
                <div className="ml-6 flex-1">
                  <div className="grid grid-cols-1 gap-2 sm:grid-cols-1">
                    {labels.map((lbl: string, idx: number) => {
                      const percent = Math.round((data[idx] / totalsum) * 100);
                      return (
                        <div key={lbl} className="flex items-center justify-between text-sm">
                          <div className="flex items-center gap-2 min-w-0">
                            <span className="inline-block h-2.5 w-2.5 rounded-sm" style={{ backgroundColor: colors[idx] }} />
                            <span className="truncate text-[#0B1739] font-medium">{lbl}</span>
                          </div>
                          <span className="ml-4 text-[#0B1739] font-semibold">{percent}%</span>
                        </div>
                      );
                    })}
                  </div>
                </div>
              </div>
            );
          })()}
        </div>
      </div>
      {/* Severity Distribution */}
      <div className="mt-6 rounded-2xl bg-white p-6 shadow-lg ring-1 ring-gray-100">
        <div className="mb-4 flex items-center justify-between">
          <h3 className="text-lg font-semibold text-[#0B1739]">Severity Distribution</h3>
          <span className="inline-flex h-8 w-8 items-center justify-center rounded-full">
            <img src="/icons/box6.svg" alt="severity" className="h-4 w-4" />
          </span>
        </div>
        {loading ? (
          <div className="grid grid-cols-1 gap-6 md:grid-cols-2 xl:grid-cols-12">
            {[1, 2, 3, 4].map(idx => (
              <div key={idx} className="animate-pulse rounded-xl bg-gray-50 p-5 xl:col-span-3">
                <div className="mb-4 flex items-center justify-between">
                  <div className="h-4 w-16 rounded bg-gray-200" />
                  <div className="h-5 w-8 rounded bg-gray-200" />
                </div>
                <div className="h-2 w-full rounded-full bg-gray-200" />
              </div>
            ))}
          </div>
        ) : (() => {
          const d = overview?.severityDistribution || { critical: 0, high: 0, medium: 0, low: 0 };
          const total = d.critical + d.high + d.medium + d.low || 1;
          const items = [
            { label: "Critical", value: d.critical, color: "bg-red-500", text: "text-red-500" },
            { label: "High", value: d.high, color: "bg-orange-400", text: "text-orange-500" },
            { label: "Medium", value: d.medium, color: "bg-yellow-500", text: "text-yellow-500" },
            { label: "Low", value: d.low, color: "bg-blue-600", text: "text-blue-600" }
          ];
          return (
            <div className="grid grid-cols-1 gap-6 md:grid-cols-2 xl:grid-cols-12">
              {items.map(it => (
                <div key={it.label} className="rounded-xl bg-gray-50 p-5 xl:col-span-3">
                  <div className="mb-4 flex items-center justify-between">
                    <span className="text-sm text-gray-700">{it.label}</span>
                    <span className={`text-lg font-semibold ${it.text}`}>{it.value}</span>
                  </div>
                  <div className="h-2 w-full rounded-full bg-gray-200">
                    <div
                      className={`h-2 rounded-full ${it.color}`}
                      style={{ width: `${Math.min(100, Math.round((it.value / total) * 100))}%` }}
                    />
                  </div>
                </div>
              ))}
            </div>
          );
        })()}
      </div>
      {/* Pretesting Programs */}
      <div className="mt-6 rounded-2xl bg-white p-6 shadow-lg ring-1 ring-gray-100">
        <div className="mb-2 flex items-center justify-between">
          <h3 className="text-lg font-semibold text-[#0B1739]">Pretesting Programs</h3>
          <span className="inline-flex h-8 w-8 items-center justify-center rounded-full">
            <PieChartIcon className="h-4 w-4 text-[#0B1739]" />
          </span>
        </div>
        <p className="-mt-1 mb-4 text-sm text-gray-500">Programs in Pretesting Phase</p>
        <PretestingProgramsList />
      </div>
    </div>
  );
};

export default ResearcherDashboard;